import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { useAddAlert } from '@/hooks/useAddAlert'
import { useSearchParams } from '@/hooks/useSearchParams'
import type { LeadProfileLead } from '../components/LeadProfileModal/LeadProfileModal.types'
import {
  clearCrmLeadDeepLinkSeed,
  createCrmLeadDeepLinkStub,
  peekCrmLeadDeepLinkSeed,
} from './crmLeadDeepLinkSeed'
import {
  getCrmLeadDeepLinkPendingLeadId,
  setCrmLeadDeepLinkPendingLeadId,
  subscribeCrmLeadDeepLinkPendingLeadId,
} from './crmLeadDeepLinkPending'

export { setCrmLeadDeepLinkSeed } from './crmLeadDeepLinkSeed'
export { setCrmLeadDeepLinkPendingLeadId } from './crmLeadDeepLinkPending'

export const CRM_LEAD_DEEP_LINK_SEARCH_PARAM = 'lead-id'
export const CRM_LEAD_FUNNEL_DEEP_LINK_SEARCH_PARAM = 'funnel-id'

const parsePositiveInt = (value: string | null | undefined): number | null => {
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue) || numericValue <= 0) return null
  return Math.trunc(numericValue)
}

export const useClearCrmLeadDeepLinkSearchParams = () => {
  const [, , removeLeadId] = useSearchParams(CRM_LEAD_DEEP_LINK_SEARCH_PARAM)
  const [, , removeFunnelId] = useSearchParams(
    CRM_LEAD_FUNNEL_DEEP_LINK_SEARCH_PARAM,
  )

  return useCallback(() => {
    removeLeadId()
    removeFunnelId()
  }, [removeFunnelId, removeLeadId])
}

export const useOpenCrmLeadDeepLinkSearchParams = () => {
  const [, setLeadId] = useSearchParams(CRM_LEAD_DEEP_LINK_SEARCH_PARAM)
  const [, setFunnelId] = useSearchParams(CRM_LEAD_FUNNEL_DEEP_LINK_SEARCH_PARAM)
  const [, , removeFunnelId] = useSearchParams(
    CRM_LEAD_FUNNEL_DEEP_LINK_SEARCH_PARAM,
  )

  return useCallback(
    (leadId: number, funnelId?: number | null) => {
      const numericLeadId = Number(leadId)
      if (!Number.isFinite(numericLeadId) || numericLeadId <= 0) return

      setCrmLeadDeepLinkPendingLeadId(Math.trunc(numericLeadId))
      setLeadId(String(Math.trunc(numericLeadId)))

      const numericFunnelId =
        funnelId != null && funnelId !== undefined ? Number(funnelId) : null
      if (
        numericFunnelId != null &&
        Number.isFinite(numericFunnelId) &&
        numericFunnelId > 0
      ) {
        setFunnelId(String(Math.trunc(numericFunnelId)))
      } else {
        removeFunnelId()
      }
    },
    [removeFunnelId, setFunnelId, setLeadId],
  )
}

export const useCrmLeadDeepLinkLeadIdFromUrl = () => {
  const [leadIdFromUrl] = useSearchParams(CRM_LEAD_DEEP_LINK_SEARCH_PARAM)
  return parsePositiveInt(leadIdFromUrl)
}

export const useSyncCrmLeadFunnelIdInUrl = (
  funnelId: number | null | undefined,
  enabled: boolean,
) => {
  const [leadIdFromUrl] = useSearchParams(CRM_LEAD_DEEP_LINK_SEARCH_PARAM)
  const [funnelIdFromUrl] = useSearchParams(CRM_LEAD_FUNNEL_DEEP_LINK_SEARCH_PARAM)
  const [, setFunnelId] = useSearchParams(CRM_LEAD_FUNNEL_DEEP_LINK_SEARCH_PARAM)
  const [, , removeFunnelId] = useSearchParams(CRM_LEAD_FUNNEL_DEEP_LINK_SEARCH_PARAM)

  useEffect(() => {
    if (!enabled || !leadIdFromUrl) return

    const numericFunnelId = parsePositiveInt(
      funnelId != null ? String(funnelId) : null,
    )

    if (numericFunnelId === null) {
      if (funnelIdFromUrl) {
        removeFunnelId()
      }
      return
    }

    const nextFunnelId = String(numericFunnelId)
    if (funnelIdFromUrl === nextFunnelId) return

    setFunnelId(nextFunnelId)
  }, [
    enabled,
    funnelId,
    funnelIdFromUrl,
    leadIdFromUrl,
    removeFunnelId,
    setFunnelId,
  ])
}

type UseCrmLeadDeepLinkOptions = {
  enabled?: boolean
}

export const useCrmLeadDeepLink = ({
  enabled = true,
}: UseCrmLeadDeepLinkOptions = {}) => {
  const addAlert = useAddAlert()
  const addAlertRef = useRef(addAlert)
  addAlertRef.current = addAlert

  const [leadIdFromUrl] = useSearchParams(CRM_LEAD_DEEP_LINK_SEARCH_PARAM)
  const pendingLeadId = useSyncExternalStore(
    subscribeCrmLeadDeepLinkPendingLeadId,
    getCrmLeadDeepLinkPendingLeadId,
    getCrmLeadDeepLinkPendingLeadId,
  )
  const [funnelIdFromUrl] = useSearchParams(CRM_LEAD_FUNNEL_DEEP_LINK_SEARCH_PARAM)
  const [, , removeLeadId] = useSearchParams(CRM_LEAD_DEEP_LINK_SEARCH_PARAM)
  const [, , removeFunnelId] = useSearchParams(CRM_LEAD_FUNNEL_DEEP_LINK_SEARCH_PARAM)

  const [lead, setLead] = useState<LeadProfileLead | undefined>()
  /** Пока URL debounce не убрал lead-id — не открывать модалку заново после закрытия. */
  const [isDismissed, setIsDismissed] = useState(false)
  const preparedLeadIdRef = useRef<string | null>(null)

  const clearDeepLink = useCallback(() => {
    const leadId =
      parsePositiveInt(leadIdFromUrl) ?? getCrmLeadDeepLinkPendingLeadId()
    if (leadId != null && Number.isFinite(leadId)) {
      clearCrmLeadDeepLinkSeed(leadId)
    }
    setIsDismissed(true)
    setCrmLeadDeepLinkPendingLeadId(null)
    removeLeadId()
    removeFunnelId()
    setLead(undefined)
    preparedLeadIdRef.current = null
  }, [leadIdFromUrl, removeFunnelId, removeLeadId])

  const boardFunnelId = useMemo(() => {
    return parsePositiveInt(funnelIdFromUrl) ?? undefined
  }, [funnelIdFromUrl])

  const requestedLeadId = useMemo(() => {
    return parsePositiveInt(leadIdFromUrl) ?? pendingLeadId
  }, [leadIdFromUrl, pendingLeadId])

  useEffect(() => {
    if (leadIdFromUrl) {
      setCrmLeadDeepLinkPendingLeadId(null)
    }
  }, [leadIdFromUrl])

  useEffect(() => {
    if (pendingLeadId != null) {
      setIsDismissed(false)
    }
  }, [pendingLeadId])

  useEffect(() => {
    if (requestedLeadId === null) {
      setIsDismissed(false)
      setLead(undefined)
      preparedLeadIdRef.current = null
    }
  }, [requestedLeadId])

  // Только seed/stub — getLead делает bootstrap модалки (LeadRefetchContext)
  useEffect(() => {
    if (!enabled || isDismissed) return

    if (requestedLeadId === null) {
      if (leadIdFromUrl) {
        addAlertRef.current({
          text: 'Некорректный id лида в ссылке',
          type: 'error',
        })
        clearDeepLink()
      }
      return
    }

    const leadIdKey = String(requestedLeadId)
    const seed = peekCrmLeadDeepLinkSeed(requestedLeadId)

    // Seed важнее stub: даже если id уже «подготовлен», подтягиваем строку таблицы/канбана.
    if (seed) {
      clearCrmLeadDeepLinkSeed(requestedLeadId)
      preparedLeadIdRef.current = leadIdKey
      setLead(seed)
      return
    }

    if (preparedLeadIdRef.current === leadIdKey) {
      // URL funnel-id мог прийти позже pending lead-id — обогащаем stub.
      if (boardFunnelId != null) {
        setLead((prev) => {
          if (!prev || Number(prev.id) !== requestedLeadId) {
            return createCrmLeadDeepLinkStub(requestedLeadId, boardFunnelId)
          }
          const hasFunnel =
            Number(prev.currentFunnelId) > 0 ||
            (Array.isArray(prev.funnels) && prev.funnels.length > 0)
          if (hasFunnel) return prev
          return createCrmLeadDeepLinkStub(requestedLeadId, boardFunnelId)
        })
      }
      return
    }

    preparedLeadIdRef.current = leadIdKey
    setLead(createCrmLeadDeepLinkStub(requestedLeadId, boardFunnelId ?? null))
  }, [
    boardFunnelId,
    clearDeepLink,
    enabled,
    isDismissed,
    leadIdFromUrl,
    requestedLeadId,
  ])

  const isOpen = enabled && !isDismissed && requestedLeadId !== null

  const displayLead = useMemo(() => {
    if (!isOpen || requestedLeadId === null) return undefined
    // Синхронно: иначе первый кадр = stub без воронки → двойной bootstrap/blur
    const seed = peekCrmLeadDeepLinkSeed(requestedLeadId)
    if (seed) return seed
    if (lead) return lead
    return createCrmLeadDeepLinkStub(requestedLeadId, boardFunnelId ?? null)
  }, [boardFunnelId, isOpen, lead, requestedLeadId])

  return {
    lead: displayLead,
    isOpen,
    /** Данные карточки грузит модалка; флаг оставлен для совместимости (спиннер на карточке канбана). */
    loading: false,
    boardFunnelId,
    clearDeepLink,
  }
}
